"use client";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { TfiAngleDown, TfiAngleRight } from "react-icons/tfi";
import { OpsiPageBuilder } from "../opsi/OpsiPageBuilder";
import { OpsiTopicNav } from "./OpsiTopicNav";

const API_BASE =
  process.env.NEXT_PUBLIC_BE || "https://api.liverpoolcityregion-ca.gov.uk";

// opsi-topic rejects orderby=date; id works and we sort client-side anyway.
const OPSI_TOPIC_QUERY =
  "per_page=100&orderby=id&order=asc";
const OPSI_POSTS_QUERY =
  "per_page=100&orderby=date&order=asc&_fields=id,title,slug,date,modified,acf,opsi-topic";

const getTopicIdsForPost = (post) => {
  const rawTopicIds =
    post?.["opsi-topic"] ??
    post?.opsi_topic ??
    post?.opsiTopic ??
    post?.topics ??
    [];

  if (!Array.isArray(rawTopicIds)) {
    return [];
  }

  return rawTopicIds
    .map((topicId) => Number(topicId))
    .filter((topicId) => Number.isFinite(topicId));
};

const getRenderedTitle = (entity) => entity?.title?.rendered || entity?.name || "";

const normalizeLabel = (value) =>
  String(value || "")
    .replace(/<[^>]*>/g, " ")
    .replace(/&amp;/gi, "&")
    .replace(/&#0*38;/g, "&")
    .replace(/&#x0*26;/gi, "&")
    .replace(/&nbsp;/gi, " ")
    .replace(/\s+/g, " ")
    .trim()
    .toLowerCase();

const isWholeNumberNavItem = (value) => /^\d+\.(?!\d)/.test(normalizeLabel(value));

const getEntitySortTime = (entity) => {
  const raw = entity?.date || entity?.modified || "";
  const time = raw ? Date.parse(raw) : NaN;

  if (Number.isFinite(time)) {
    return time;
  }

  return entity?.id ?? 0;
};

const sortEntitiesOldToNew = (entities) =>
  [...entities].sort((a, b) => getEntitySortTime(a) - getEntitySortTime(b));

const getTopicOrderValues = (blockData) => {
  if (!blockData || typeof blockData !== "object") {
    return [];
  }

  const knownOrderKeys = [
    "topic_order",
    "opsi_topic_order",
    "topic_ordering",
    "topics_order",
    "ordered_topics",
    "opsi_topics_order",
  ];

  const parseArrayValue = (arr) => {
    if (!Array.isArray(arr)) {
      return [];
    }

    return arr
      .map((item) => {
        if (typeof item === "string") {
          return item;
        }

        if (item && typeof item === "object") {
          return (
            item.text ||
            item.name ||
            item.title ||
            item.label ||
            item.value ||
            item.topic ||
            ""
          );
        }

        return "";
      })
      .map((value) => String(value || "").trim())
      .filter(Boolean);
  };

  for (const key of knownOrderKeys) {
    const values = parseArrayValue(blockData?.[key]);
    if (values.length) {
      return values;
    }
  }

  const discoveredValues = Object.values(blockData).flatMap((value) =>
    parseArrayValue(value),
  );

  return [...new Set(discoveredValues)];
};

const OPSI_HEADING_COLOR = "#993189";

export const OpsiBlueprint = ({
  data,
  id,
  initialTopics = null,
  initialPosts = null,
  initialError = null,
}) => {
  const blockData = data?.opsi_blueprint ?? data;
  const toolkitScrollAnchorRef = useRef(null);

  const scrollToToolkitAnchor = useCallback(() => {
    // Wait for topic/content state to paint before scrolling.
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        toolkitScrollAnchorRef.current?.scrollIntoView({
          behavior: "smooth",
          block: "start",
        });
      });
    });
  }, []);

  const hasInitialData =
    Array.isArray(initialTopics) && Array.isArray(initialPosts);

  const [topics, setTopics] = useState(initialTopics ?? []);
  const [posts, setPosts] = useState(initialPosts ?? []);
  const [activeTopicId, setActiveTopicId] = useState(null);
  const [expandedTopicId, setExpandedTopicId] = useState(null);
  const [selectedPostId, setSelectedPostId] = useState(null);
  const [isLoading, setIsLoading] = useState(!hasInitialData);
  const [errorMessage, setErrorMessage] = useState(initialError ?? "");

  useEffect(() => {
    if (hasInitialData) {
      return;
    }

    let cancelled = false;

    const fetchData = async () => {
      setIsLoading(true);
      setErrorMessage("");

      try {
        const [topicResponse, postResponse] = await Promise.all([
          fetch(
            `${API_BASE}/wp-json/wp/v2/opsi-topic?${OPSI_TOPIC_QUERY}`,
          ),
          fetch(
            `${API_BASE}/wp-json/wp/v2/opsi?${OPSI_POSTS_QUERY}`,
          ),
        ]);

        if (!topicResponse.ok || !postResponse.ok) {
          throw new Error("Failed to load OPSI data.");
        }

        const [topicsData, postsData] = await Promise.all([
          topicResponse.json(),
          postResponse.json(),
        ]);

        if (cancelled) {
          return;
        }

        setTopics(Array.isArray(topicsData) ? topicsData : []);
        setPosts(Array.isArray(postsData) ? postsData : []);
      } catch (error) {
        if (!cancelled) {
          setErrorMessage("Could not load OPSI topics right now.");
          setTopics([]);
          setPosts([]);
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    };

    fetchData();

    return () => {
      cancelled = true;
    };
  }, [hasInitialData]);

  const topicGroups = useMemo(() => {
    const groups = (topics || []).map((topic) => {
      const matchedPosts = sortEntitiesOldToNew(
        (posts || []).filter((post) =>
          getTopicIdsForPost(post).includes(topic.id),
        ),
      );

      return {
        ...topic,
        pages: matchedPosts,
      };
    });

    const groupsWithPages = groups.filter((group) => group.pages.length > 0);
    const configuredTopicOrder = getTopicOrderValues(blockData).map((value) =>
      normalizeLabel(value),
    );
    const orderIndex = new Map(
      configuredTopicOrder.map((value, index) => [value, index]),
    );

    const orderedGroups = configuredTopicOrder.length
      ? groupsWithPages.filter((group) =>
          orderIndex.has(normalizeLabel(getRenderedTitle(group))),
        )
      : groupsWithPages;

    return orderedGroups.sort((a, b) => {
      const aLabel = normalizeLabel(getRenderedTitle(a));
      const bLabel = normalizeLabel(getRenderedTitle(b));
      const aIndex = orderIndex.has(aLabel) ? orderIndex.get(aLabel) : Infinity;
      const bIndex = orderIndex.has(bLabel) ? orderIndex.get(bLabel) : Infinity;

      if (aIndex !== bIndex) {
        return aIndex - bIndex;
      }

      return getEntitySortTime(a) - getEntitySortTime(b);
    });
  }, [topics, posts, blockData]);

  const activeTopic = useMemo(
    () => topicGroups.find((topic) => topic.id === activeTopicId) ?? null,
    [topicGroups, activeTopicId],
  );

  const selectedPost = useMemo(() => {
    if (!activeTopic) {
      return null;
    }

    const post = (posts || []).find((item) => item.id === selectedPostId);
    const belongsToTopic =
      post && getTopicIdsForPost(post).includes(activeTopic.id);

    if (belongsToTopic) {
      return post;
    }

    return activeTopic.pages?.[0] ?? null;
  }, [posts, selectedPostId, activeTopic]);

  useEffect(() => {
    if (!topicGroups.length) {
      setActiveTopicId(null);
      setExpandedTopicId(null);
      setSelectedPostId(null);
      return;
    }

    const ensureValidTopicId = (prev) => {
      if (topicGroups.some((topic) => topic.id === prev)) {
        return prev;
      }
      return topicGroups[0].id;
    };

    setActiveTopicId(ensureValidTopicId);
    setExpandedTopicId(ensureValidTopicId);
  }, [topicGroups]);

  useEffect(() => {
    if (!activeTopicId || !topicGroups.length) {
      return;
    }

    const topic = topicGroups.find((item) => item.id === activeTopicId);
    if (!topic) {
      return;
    }

    setSelectedPostId((prev) => {
      const post = (posts || []).find((item) => item.id === prev);
      if (post && getTopicIdsForPost(post).includes(activeTopicId)) {
        return prev;
      }
      return topic.pages?.[0]?.id ?? null;
    });
  }, [activeTopicId, topicGroups, posts]);

  const selectTopic = (topicId, pageId = null) => {
    const topic = topicGroups.find((item) => item.id === topicId);
    if (!topic) {
      return;
    }

    setActiveTopicId(topicId);
    setExpandedTopicId(topicId);
    setSelectedPostId(pageId ?? topic.pages?.[0]?.id ?? null);
  };

  const toggleTopic = (topicId) => {
    if (expandedTopicId === topicId) {
      if (activeTopicId && activeTopicId !== topicId) {
        setExpandedTopicId(activeTopicId);
      } else {
        setExpandedTopicId(null);
      }
      return;
    }

    selectTopic(topicId);
  };

  const selectPageInTopic = (topicId, pageId) => {
    selectTopic(topicId, pageId);
  };

  const activeTopicIndex = useMemo(
    () => topicGroups.findIndex((topic) => topic.id === activeTopicId),
    [topicGroups, activeTopicId],
  );

  const selectedPageIndex = useMemo(() => {
    if (!activeTopic?.pages?.length || !selectedPost) {
      return -1;
    }

    return activeTopic.pages.findIndex((page) => page.id === selectedPost.id);
  }, [activeTopic, selectedPost]);

  const isAtFirstItem =
    activeTopicIndex <= 0 && selectedPageIndex <= 0;

  const isAtLastItem =
    activeTopicIndex >= topicGroups.length - 1 &&
    selectedPageIndex >= 0 &&
    selectedPageIndex >= (activeTopic?.pages?.length ?? 0) - 1;

  const goByOffset = (offset) => {
    if (!activeTopic?.pages?.length || !topicGroups.length) {
      return;
    }

    const topicIndex = activeTopicIndex >= 0 ? activeTopicIndex : 0;
    const pageIndex = selectedPageIndex >= 0 ? selectedPageIndex : 0;
    const nextPageIndex = pageIndex + offset;

    if (nextPageIndex >= 0 && nextPageIndex < activeTopic.pages.length) {
      setSelectedPostId(activeTopic.pages[nextPageIndex].id);
      scrollToToolkitAnchor();
      return;
    }

    if (offset > 0 && nextPageIndex >= activeTopic.pages.length) {
      const nextTopic = topicGroups[topicIndex + 1];
      if (nextTopic?.pages?.length) {
        selectTopic(nextTopic.id, nextTopic.pages[0].id);
        scrollToToolkitAnchor();
      }
      return;
    }

    if (offset < 0 && nextPageIndex < 0) {
      const prevTopic = topicGroups[topicIndex - 1];
      const lastPage = prevTopic?.pages?.[prevTopic.pages.length - 1];
      if (lastPage) {
        selectTopic(prevTopic.id, lastPage.id);
        scrollToToolkitAnchor();
      }
    }
  };

  const scrollAnchorId = id ? `${id}-scroll-anchor` : "opsi-toolkit-scroll-anchor";

  return (
    <section
      id={id}
      className="OpsiBlueprint relative w-full bg-[#FFFFFF] py-10 md:py-16"
    >
      {/* Invisible anchor: scroll target sits 100px above the toolkit */}
      <div
        ref={toolkitScrollAnchorRef}
        id={scrollAnchorId}
        className="pointer-events-none absolute left-0 h-px w-px opacity-0"
        style={{ top: -100 }}
        aria-hidden="true"
        tabIndex={-1}
      />

      <div className="mx-auto flex w-full flex-col gap-10 px-6 md:flex-row md:items-start md:px-12 lg:px-[72px]">
        <div className="rounded-[20px] bg-[#F7F7F7] p-6 md:w-full md:max-w-[328px] md:flex-shrink-0 md:p-8">


          {isLoading ? (
            <p className="text-[1rem] text-[#4a4d57]">Loading OPSI topics...</p>
          ) : null}

          {!isLoading && errorMessage ? (
            <p className="text-[1rem] text-[#4a4d57]">{errorMessage}</p>
          ) : null}

          {!isLoading && !errorMessage && !topicGroups.length ? (
            <p className="text-[1rem] text-[#4a4d57]">No OPSI topics found.</p>
          ) : null}

          {!isLoading && !errorMessage
            ? topicGroups.map((topic) => {
                const expanded = expandedTopicId === topic.id;

                return (
                  <div key={topic.id} className="border-b border-[#e2e4ea] py-3">
                    <button
                      type="button"
                      onClick={() => toggleTopic(topic.id)}
                      className="flex w-full items-center justify-between gap-4 text-left"
                    >
                      <span
                        className="text-[1.1rem] font-semibold !text-[#000]"
                        style={{ color: OPSI_HEADING_COLOR }}
                        dangerouslySetInnerHTML={{
                          __html: getRenderedTitle(topic),
                        }}
                      />
                      <span
                        className="flex h-[34px] w-[34px] min-h-[34px] min-w-[34px] flex-none items-center justify-center rounded-full border bg-[#993189]"
                        style={{ borderColor: OPSI_HEADING_COLOR }}
                      >
                        {expanded ? (
                          <TfiAngleDown className="!text-[#FFF]" size={14} color={OPSI_HEADING_COLOR} />
                        ) : (
                          <TfiAngleRight className="!text-[#FFF]" size={14} color={OPSI_HEADING_COLOR} />
                        )}
                      </span>
                    </button>

                    {expanded ? (
                      <div className="mt-3 flex flex-col">
                        {topic.pages.map((page) => {
                          const pageTitle = getRenderedTitle(page);
                          const isSectionItem = isWholeNumberNavItem(pageTitle);
                          return (
                            <button
                              key={page.id}
                              type="button"
                              onClick={() => selectPageInTopic(topic.id, page.id)}
                              className={`w-full pl-6 border-b-[1px] border-b-[#993189] py-3 text-left !text-[16px] leading-[1.2] transition text-[#000] hover:text-[#000] ${
                                isSectionItem ? "!font-bold" : "!font-normal"
                              }`}
                              dangerouslySetInnerHTML={{
                                __html: pageTitle,
                              }}
                            />
                          );
                        })}
                      </div>
                    ) : null}
                  </div>
                );
              })
            : null}
        </div>

        <div className="flex min-h-[320px] flex-1 flex-col overflow-hidden rounded-[20px]  p-6 md:min-w-0 md:p-10">
          {selectedPost ? (
            <div className="flex h-full min-h-[320px] w-full flex-1 flex-col">
              <div className="w-full">
                <header className="mb-6">
                  {activeTopic ? (
                    <p
                      className="mb-[20px] text-[1rem] font-semibold leading-tight md:text-[1.1rem]"
                      style={{ color: "#993189" }}
                      dangerouslySetInnerHTML={{
                        __html: getRenderedTitle(activeTopic),
                      }}
                    />
                  ) : null}
                  <h3
                    className="!text-[50px] !font-bold lg:w-4/5 selection:leading-[1.15]"
                    style={{ color: OPSI_HEADING_COLOR }}
                    dangerouslySetInnerHTML={{
                      __html: getRenderedTitle(selectedPost),
                    }}
                  />
                </header>
                {/* OPSI CPT sections — separate page builder from the main site PageBuilder */}
                <OpsiPageBuilder key={selectedPost.id} post={selectedPost} />
              </div>

              {activeTopic?.pages?.length && topicGroups.length ? (
                <div className=" w-full pt-[100px]">
                  <OpsiTopicNav
                    isFirstItem={isAtFirstItem}
                    isLastItem={isAtLastItem}
                    onPrevious={() => goByOffset(-1)}
                    onNext={() => goByOffset(1)}
                  />
                </div>
              ) : null}
            </div>
          ) : (
            <p className="text-[1rem] text-[#4a4d57]">
              Select a page title from the left to preview it here.
            </p>
          )}
        </div>
      </div>
    </section>
  );
};
